import { useState, useEffect } from 'react';
import { apiRequest } from '@/api/client';
import { useToast } from '@/hooks/use-toast';

/**
 * Email Domain interface
 */
export interface EmailDomain {
  id: string;
  domain: string;
  organisation_id: string;
  default_from_email: string;
  default_from_name: string;
  is_verified: boolean;
  verification_status: string;
  cloudflare_zone_status: string;
  created_at: string;
}

/**
 * Hook for fetching verified email domains for an organisation
 *
 * @param organisationId - Organisation ID to fetch domains for
 * @returns Object containing domains, loading state, error, and refresh function
 */
export const useVerifiedDomains = (organisationId: string | null) => {
  const [domains, setDomains] = useState<EmailDomain[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  /**
   * Fetch verified domains from API
   */
  const fetchDomains = async () => {
    if (!organisationId) {
      setDomains([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch verified domains for this organisation using non-admin API
      // This endpoint is accessible to all authenticated users with access to the organisation
      const response = await apiRequest<{ success: boolean; data: EmailDomain[] }>({
        method: 'GET',
        url: `/api/email/domains?organisation_id=${organisationId}&verified_only=true`,
      });

      if (response.success && response.data) {
        // Filter to only verified domains (double-check even though API filters)
        const verifiedDomains = response.data.filter(
          (domain) => domain.is_verified && domain.verification_status === 'success'
        );
        setDomains(verifiedDomains);
      } else {
        setDomains([]);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch verified domains';
      setError(errorMessage);
      console.error('[useVerifiedDomains] Fetch error:', err);

      toast({
        title: 'Error Loading Domains',
        description: errorMessage,
        variant: 'destructive',
      });

      setDomains([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresh domains (useful after domain verification)
   */
  const refresh = () => {
    fetchDomains();
  };

  // Fetch on mount and when organisationId changes
  useEffect(() => {
    fetchDomains();
  }, [organisationId]);

  return {
    domains,
    loading,
    error,
    refresh,
    hasVerifiedDomains: domains.length > 0,
  };
};
